import { useMemo, useState, useEffect, useCallback } from 'react'
import { useParams, useSearch, useNavigate, Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import { Input } from './ui/input'
import { MultilingualInput } from './ui/multilingual-input'
import { AppIcon } from './ui/icon'
import { AssetTypeHierarchy, type AssetTypeTreeMode } from './assets/AssetTypeHierarchy'
import { possibleChildTypes as listPossibleChildTypes } from '../lib/asset-type-tree'
import { useAssetTypes, useAssetTypeProperties, useAssetsByAssetType, usePropertyDefinitions, useAddAssetTypeProperty, useRemoveAssetTypeProperty, useUpdateAssetType, useUpdateAssetTypeProperty, useFolderLayouts, useAssetTypeUsage, useDeleteAssetType, useArchiveAssetType, useRestoreAssetType } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { useApi } from '../api/hooks'
import { useCan } from '../contexts/PermissionsContext'
import { useTranslation } from 'react-i18next'
import { formatCreated } from '../lib/format'
import { getLocalizedName, localizedPatchValue, toBilingualForm } from '../lib/localization'
import { PageHeader } from './shared/PageHeader'
import { DeleteConfirmDialog } from './ui/delete-confirm-dialog'
import { AssetTypeIconPicker } from './assets/AssetTypeIconPicker'
import { AssetTypeTile, type AssetTypeLike } from './assets/AssetTypeTile'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { mdiArrowLeft, mdiCheck, mdiClockOutline, mdiClose, mdiCog, mdiArchiveOutline, mdiArchiveArrowUpOutline, mdiDeleteOutline, mdiEyeOutline, mdiPlus, mdiShapeOutline } from '@mdi/js'

/**
 * Sentinel for the "no default layout" option: Radix's Select forbids an
 * empty-string item value. Mapped back to `null` on submit, which the API reads
 * as "clear the column" (models.NullableUUID). Spec 2026-08-01-05.
 */
const NO_FOLDER_LAYOUT = '__none__'

const DATA_TYPE_LABELS = {
  text: 'Text',
  number: 'Number',
  boolean: 'Boolean',
  date: 'Date',
  enum: 'Enum',
  json: 'JSON',
} as const

export function AssetTypeManagePage() {
  const { org, assetTypeUid } = useParams({ strict: false })
  const search = useSearch({ from: '/$org/asset-types/$assetTypeUid' })
  const navigate = useNavigate({ from: '/$org/asset-types/$assetTypeUid' })
  const { toast } = useToast()
  const { request } = useApi()
  const { t, i18n } = useTranslation('types')
  // The asset-type catalogue is a type definition, not asset data: editing it
  // is `configuration.write`. Without it the page stays fully readable — every
  // value keeps rendering, only the affordances that would write disappear or
  // fall back to a read-only rendering (spec 2026-09-04-02, "Gating").
  const canWrite = useCan('configuration.write')
  const [selectedProperty, setSelectedProperty] = useState<string>('')
  const [initialPropertyOptions, setInitialPropertyOptions] = useState<ApiSearchableSelectOption[]>([])
  const [allAvailableOptions, setAllAvailableOptions] = useState<ApiSearchableSelectOption[]>([])
  const rawTab = search.tab || 'overview'
  // Settings merged into overview — collapse old deep links silently.
  const activeTab = rawTab === 'settings' ? 'overview' : rawTab
  const [possibleParentTypes, setPossibleParentTypes] = useState<string[]>([])
  const [showInheritedProperties, setShowInheritedProperties] = useState(true)
  const [isEditingLifespan, setIsEditingLifespan] = useState(false)
  const [editingWeightUid, setEditingWeightUid] = useState<string | null>(null)
  const [editingWeightValue, setEditingWeightValue] = useState<string>('')

  // Inline-edit state for Name on the Overview tab
  const [isEditingName, setIsEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState<Record<string, string>>({ en: '', fr: '' })

  // Inline-edit state for Description, edited next to the name (spec 2026-09-25-01)
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState<Record<string, string>>({ en: '', fr: '' })
  const [descriptionSaveFailed, setDescriptionSaveFailed] = useState(false)

  // Inline add-parent state
  const [isAddingParent, setIsAddingParent] = useState(false)

  interface LifespanEntry {
    condition: number
    offset_years_min: number
    offset_years_max: number
    theoretical_lifespan_years: number
    source: string
  }

  const DEFAULT_LIFESPAN_ENTRIES: LifespanEntry[] = [1, 2, 3, 4, 5].map(c => ({
    condition: c,
    offset_years_min: 0,
    offset_years_max: 0,
    theoretical_lifespan_years: 0,
    source: '',
  }))

  const [lifespanEntries, setLifespanEntries] = useState<LifespanEntry[]>([])

  // Queries
  const { data: assetTypes = [] } = useAssetTypes(org || 'demo', true)
  const { data: assetTypeProperties = [], isLoading: isLoadingProperties, error: propertiesError } = useAssetTypeProperties(org || 'demo', assetTypeUid || '', showInheritedProperties)
  const { data: assetsInvolved = [] } = useAssetsByAssetType(org || 'demo', assetTypeUid || '', { enabled: showInheritedProperties })
  const { data: allProperties = [] } = usePropertyDefinitions(org || 'demo', { entityType: 'asset' })
  // An asset type's default folder structure must be an asset layout.
  const { data: folderLayouts = [] } = useFolderLayouts(org || 'demo', true, 'asset')

  // Mutations
  const addPropertyMutation = useAddAssetTypeProperty(org || 'demo', assetTypeUid || '')
  const removePropertyMutation = useRemoveAssetTypeProperty(org || 'demo', assetTypeUid || '')
  const updateAssetTypeMutation = useUpdateAssetType(org || 'demo', assetTypeUid || '')
  const updatePropertyMutation = useUpdateAssetTypeProperty(org || 'demo', assetTypeUid || '')

  // Find current asset type
  const currentAssetType = assetTypes.find(at => at.uid === assetTypeUid)

  // Delete / archive / restore (spec 2026-10-11-28). Delete stays disabled while
  // an asset (archived ones included) or a child type uses this type.
  const usage = useAssetTypeUsage(org || 'demo', assetTypeUid || '')
  const childTypeCount = assetTypes.filter(at => at.inherits_from_asset_type_uid === assetTypeUid).length
  const isArchived = Boolean((currentAssetType as { archived_at?: string | null } | undefined)?.archived_at)
  const deleteMutation = useDeleteAssetType(org || 'demo')
  const archiveMutation = useArchiveAssetType(org || 'demo')
  const restoreMutation = useRestoreAssetType(org || 'demo')
  const deleteBlocked = usage.isLoading || usage.total > 0 || childTypeCount > 0
  const handleArchiveToggle = async () => {
    const mutation = isArchived ? restoreMutation : archiveMutation
    try {
      await mutation.mutateAsync({ params: { path: { orgCode: org || 'demo', assetTypeUid: assetTypeUid || '' } } })
      toast({ title: t(isArchived ? 'asset_types.restore_success' : 'asset_types.archive_success') })
    } catch {
      toast({ title: t('asset_types.archive_error'), variant: 'destructive' })
    }
  }
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ params: { path: { orgCode: org || 'demo', assetTypeUid: assetTypeUid || '' } } })
      toast({ title: t('asset_types.delete_success') })
      navigate({ to: '/$org/asset-types', params: { org: org || 'demo' } } as any)
    } catch {
      toast({ title: t('asset_types.delete_error'), variant: 'destructive' })
    }
  }

  // Derived picture/multiple values (no local state — toggle commits directly)
  const shouldReceivePicture = Boolean(currentAssetType?.properties?.picture)
  const isMultipleQuantity = Boolean(currentAssetType?.properties?.multiple)

  // Helper function to handle multilingual asset type names
  const getAssetTypeName = useCallback((assetType: any) => {
    if (!assetType?.name) return assetType?.code || t('asset_types.unnamed')

    if (typeof assetType.name === 'object' && assetType.name !== null) {
      const names = assetType.name as Record<string, string>
      return getLocalizedName(names, i18n.language) || assetType.code || t('asset_types.unnamed')
    }

    return assetType.name || assetType.code || t('asset_types.unnamed')
  }, [t, i18n.language])

  const getAssetTypeNameObject = (assetType: any): Record<string, string> => {
    if (!assetType?.name) return { en: '', fr: '' }
    if (typeof assetType.name === 'object' && assetType.name !== null) {
      return { en: '', fr: '', ...(assetType.name as Record<string, string>) }
    }
    return { en: String(assetType.name), fr: '' }
  }

  // Helper function to handle multilingual property names
  const getPropertyName = useCallback((property: any) => {
    if (!property?.name) return property?.code || t('asset_types.unnamed_property')
    if (typeof property.name === 'object' && property.name !== null) {
      const names = property.name as Record<string, string>
      return getLocalizedName(names, i18n.language) || property.code || t('asset_types.unnamed_property')
    }
    return property.name || property.code || t('asset_types.unnamed_property')
  }, [t, i18n.language])

  const getPropertyDescription = (property: any) => {
    if (!property?.description) return ''
    if (typeof property.description === 'object' && property.description !== null) {
      const descriptions = property.description as Record<string, string>
      return getLocalizedName(descriptions, i18n.language)
    }
    return property.description || ''
  }

  // Get available properties (not already associated)
  const availableProperties = useMemo(() => {
    const associatedPropertyUids = new Set(assetTypeProperties.map(p => p.uid))
    return allProperties.filter(p => !associatedPropertyUids.has(p.uid))
  }, [allProperties, assetTypeProperties])

  // Get available asset types for parent type selection (excluding current type)
  const availableParentTypes = useMemo(() => {
    return assetTypes.filter(at => at.uid !== assetTypeUid)
  }, [assetTypes, assetTypeUid])

  // Types that inherit from this one, and types whose assets may sit below
  // assets of this one (they list it among their possible parents).
  const inheritingTypes = useMemo(
    () => assetTypes.filter(at => at.inherits_from_asset_type_uid === assetTypeUid),
    [assetTypes, assetTypeUid]
  )
  // A rule written against a type this one inherits from counts: equipment
  // that accepts `unit` may sit below a `unit_housing` (spec 2026-10-09-04).
  const possibleChildTypes = useMemo(
    () => (assetTypeUid ? listPossibleChildTypes(assetTypeUid, assetTypes) : []),
    [assetTypes, assetTypeUid]
  )
  const treeMode: AssetTypeTreeMode = search.tree === 'children' ? 'children' : 'inheritance'

  // Initialize possible parent types when currentAssetType changes
  useEffect(() => {
    if (currentAssetType?.possible_parent_asset_type_uids) {
      setPossibleParentTypes([...currentAssetType.possible_parent_asset_type_uids])
    } else {
      setPossibleParentTypes([])
    }
  }, [currentAssetType])

  // Initialize lifespan entries when currentAssetType changes
  useEffect(() => {
    if (currentAssetType?.properties?.lifespan_reference) {
      const entries = currentAssetType.properties.lifespan_reference as LifespanEntry[]
      const full = [1, 2, 3, 4, 5].map(c => {
        const existing = entries.find((e: LifespanEntry) => e.condition === c)
        return existing || { condition: c, offset_years_min: 0, offset_years_max: 0, theoretical_lifespan_years: 0, source: '' }
      })
      setLifespanEntries(full)
    } else {
      setLifespanEntries([])
    }
  }, [currentAssetType])

  // Set initial options from available properties
  useEffect(() => {
    const options = availableProperties.map((property: any) => ({
      value: property.uid,
      label: getPropertyName(property)
    }))
    setInitialPropertyOptions(options)
    setAllAvailableOptions(options)
  }, [availableProperties, getPropertyName])

  // Search properties function
  const searchProperties = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    try {
      const queryParams: Record<string, string> = {
        entity_type: 'asset',
        limit: '50'
      }

      if (query && query.length >= 2) {
        queryParams.q = query
      }

      const response = await request('/api/v1/organizations/{orgCode}/properties', 'GET', {
        params: {
          path: { orgCode: org || 'demo' },
          query: queryParams
        }
      })

      const properties = (response as any)?.data || []
      const associatedPropertyUids = new Set(assetTypeProperties.map(p => p.uid))

      const options = properties
        .filter((property: any) => !associatedPropertyUids.has(property.uid))
        .map((property: any) => ({
          value: property.uid,
          label: getPropertyName(property)
        }))

      return options
    } catch (error) {
      console.error('Property search error:', error)
      return []
    }
  }, [org, request, assetTypeProperties, getPropertyName])

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: t('asset_types.title'), path: `/${org}/asset-types` },
    { title: currentAssetType ? getAssetTypeName(currentAssetType) : t('asset_types.title') }
  ], [org, currentAssetType, t, getAssetTypeName])

  useBreadcrumbs(breadcrumbs)

  const handleTabChange = (tab: 'overview' | 'properties') => {
    navigate({
      search: (prev) => ({ ...prev, tab })
    })
  }

  const handlePropertySelect = useCallback((option: ApiSearchableSelectOption) => {
    if (!allAvailableOptions.find(o => o.value === option.value)) {
      setAllAvailableOptions(prev => [...prev, option])
    }
  }, [allAvailableOptions])

  const handleAddProperty = async () => {
    if (!selectedProperty) return

    try {
      await addPropertyMutation.mutateAsync(selectedProperty)
      setSelectedProperty('')
    } catch (error) {
      console.error('Error adding property association:', error)
      toast({
        title: "Error",
        description: "Failed to add property association. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleRemoveProperty = async (propertyUid: string, propertyName: string) => {
    if (!confirm(`Are you sure you want to remove the property "${propertyName}" from this asset type? This action cannot be undone.`)) {
      return
    }

    try {
      await removePropertyMutation.mutateAsync(propertyUid)
    } catch (error) {
      console.error('Error removing property association:', error)
      toast({
        title: "Error",
        description: "Failed to remove property association. Please try again.",
        variant: "destructive",
      })
    }
  }

  const persistParentTypes = async (uids: string[]) => {
    try {
      await updateAssetTypeMutation.mutateAsync({
        possible_parent_asset_type_uids: uids
      })
    } catch (error) {
      console.error('Error updating possible parent types:', error)
      toast({
        title: "Error",
        description: "Failed to update possible parent types. Please try again.",
        variant: "destructive",
      })
      // Revert local state to server value
      setPossibleParentTypes(currentAssetType?.possible_parent_asset_type_uids || [])
    }
  }

  const handleAddParentType = async (uid: string) => {
    if (possibleParentTypes.length >= 5) {
      toast({
        title: "Limit Reached",
        description: "Maximum 5 possible parent types allowed",
        variant: "destructive",
      })
      return
    }
    if (possibleParentTypes.includes(uid)) return
    const next = [...possibleParentTypes, uid]
    setPossibleParentTypes(next)
    setIsAddingParent(false)
    await persistParentTypes(next)
  }

  const handleRemoveParentType = async (uid: string) => {
    const next = possibleParentTypes.filter(u => u !== uid)
    setPossibleParentTypes(next)
    await persistParentTypes(next)
  }

  // An empty string clears the icon: the backend stores NULL and the tile
  // falls back to the family glyph.
  const handleSaveIcon = async (next: string) => {
    try {
      await updateAssetTypeMutation.mutateAsync({ icon: next })
    } catch (err) {
      console.error('Failed to update asset type icon:', err)
    }
  }

  const handleSaveName = async () => {
    try {
      await updateAssetTypeMutation.mutateAsync({ name: nameDraft })
      setIsEditingName(false)
    } catch (error) {
      console.error('Error updating name:', error)
      toast({
        title: "Error",
        description: "Failed to update name.",
        variant: "destructive",
      })
    }
  }

  // Absent = unchanged; `{}` clears the description once both languages are emptied.
  const handleSaveDescription = async () => {
    const description = localizedPatchValue(descriptionDraft, currentAssetType?.description)
    if (description === undefined) {
      setIsEditingDescription(false)
      return
    }
    try {
      setDescriptionSaveFailed(false)
      await updateAssetTypeMutation.mutateAsync({ description })
      setIsEditingDescription(false)
    } catch (error) {
      console.error('Error updating description:', error)
      setDescriptionSaveFailed(true)
    }
  }

  const handleTogglePicture = async (checked: boolean) => {
    const currentProperties = currentAssetType?.properties || {}
    const newProperties = { ...currentProperties, picture: checked }
    try {
      await updateAssetTypeMutation.mutateAsync({ properties: newProperties })
    } catch (error) {
      console.error('Error updating picture setting:', error)
      toast({
        title: "Error",
        description: "Failed to update picture setting.",
        variant: "destructive",
      })
    }
  }

  // Default document folder layout for assets of this type (spec
  // 2026-08-01-05). Committed on change, like the toggles above — the sentinel
  // maps back to an explicit null so the API clears the column.
  const handleChangeFolderLayout = async (value: string) => {
    try {
      await updateAssetTypeMutation.mutateAsync({
        document_folder_layout_uid: value === NO_FOLDER_LAYOUT ? null : value,
      })
    } catch (error) {
      console.error('Error updating default document folder layout:', error)
      toast({
        title: "Error",
        description: "Failed to update the default document folder layout.",
        variant: "destructive",
      })
    }
  }

  const handleToggleMultiple = async (checked: boolean) => {
    const currentProperties = currentAssetType?.properties || {}
    const newProperties: Record<string, any> = { ...currentProperties }
    if (checked) {
      newProperties.multiple = true
    } else {
      delete newProperties.multiple
    }
    try {
      await updateAssetTypeMutation.mutateAsync({ properties: newProperties })
    } catch (error) {
      console.error('Error updating multiple setting:', error)
      toast({
        title: "Error",
        description: "Failed to update quantity setting.",
        variant: "destructive",
      })
    }
  }

  const handleUpdateLifespanEntry = (condition: number, field: keyof LifespanEntry, value: string | number) => {
    setLifespanEntries(prev => prev.map(e =>
      e.condition === condition ? { ...e, [field]: value } : e
    ))
  }

  const handleSaveLifespan = async () => {
    try {
      const currentProperties = currentAssetType?.properties || {}
      const hasData = lifespanEntries.some(e => e.theoretical_lifespan_years > 0)
      const newProperties = {
        ...currentProperties,
        lifespan_reference: hasData ? lifespanEntries : undefined
      }
      if (!hasData) delete (newProperties as any).lifespan_reference

      await updateAssetTypeMutation.mutateAsync({ properties: newProperties })
      setIsEditingLifespan(false)
    } catch (error) {
      console.error('Error updating lifespan reference:', error)
      toast({
        title: t('asset_types.error_title'),
        description: t('asset_types.lifespan_update_failed'),
        variant: 'destructive',
      })
    }
  }

  const handleCancelEditingLifespan = () => {
    if (currentAssetType?.properties?.lifespan_reference) {
      const entries = currentAssetType.properties.lifespan_reference as LifespanEntry[]
      const full = [1, 2, 3, 4, 5].map(c => {
        const existing = entries.find((e: LifespanEntry) => e.condition === c)
        return existing || { condition: c, offset_years_min: 0, offset_years_max: 0, theoretical_lifespan_years: 0, source: '' }
      })
      setLifespanEntries(full)
    } else {
      setLifespanEntries([])
    }
    setIsEditingLifespan(false)
  }

  if (!currentAssetType) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="bg-card rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">{t('asset_types.not_found_title')}</h1>
          <p className="text-muted-foreground mb-6">
            {t('asset_types.not_found_description')}
          </p>
          <Link to={`/${org}/asset-types` as any}>
            <Button>
              <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
              {t('asset_types.back_to_list')}
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const tabs = [
    { key: 'overview' as const, label: t('asset_types.tab_overview'), testId: 'overview-tab' },
    { key: 'properties' as const, label: t('asset_types.tab_properties'), testId: 'properties-tab' },
  ]

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <PageHeader
        className="mb-6"
        icon={SECTION_ICONS.assetTypes}
        title={getAssetTypeName(currentAssetType)}
        description={t('asset_types.manage_all_aspects')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isArchived && <Badge variant="secondary" data-testid="asset-type-archived-badge">{t('asset_types.archived_badge')}</Badge>}
            <Link to="/$org/assets" params={{ org: org || 'demo' }} search={{ showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: assetTypeUid } as any}>
              <Button variant="outline" data-testid="view-assets">
                <AppIcon path={mdiEyeOutline} size="sm" className="mr-2" />
                {t('asset_types.view_assets')}
              </Button>
            </Link>
            {canWrite && (
              <Button
                variant="outline"
                onClick={handleArchiveToggle}
                disabled={archiveMutation.isPending || restoreMutation.isPending}
                data-testid={isArchived ? 'asset-type-restore' : 'asset-type-archive'}
              >
                <AppIcon path={isArchived ? mdiArchiveArrowUpOutline : mdiArchiveOutline} size="sm" className="mr-2" />
                {t(isArchived ? 'asset_types.action_restore' : 'asset_types.action_archive')}
              </Button>
            )}
            {canWrite && (
              <DeleteConfirmDialog
                trigger={
                  <Button variant="outline" disabled={deleteBlocked} data-testid="asset-type-delete">
                    <AppIcon path={mdiDeleteOutline} size="sm" className="mr-2" />
                    {t('asset_types.action_delete')}
                  </Button>
                }
                title={t('asset_types.delete_title')}
                description={t('asset_types.delete_description')}
                onConfirm={handleDelete}
                isDeleting={deleteMutation.isPending}
              />
            )}
          </div>
        }
      />

      {isArchived && (
        <p className="mb-4 text-sm text-muted-foreground" data-testid="asset-type-archived-notice">{t('asset_types.archived_notice')}</p>
      )}
      {canWrite && !usage.isLoading && (usage.total > 0 || childTypeCount > 0) && (
        <div className="mb-4 rounded-md border bg-muted/40 p-3 text-sm space-y-1" data-testid="asset-type-delete-blocked">
          {usage.total > 0 && (
            <p>
              {t('asset_types.delete_blocked_assets', { count: usage.total })}{' '}
              <Link
                to="/$org/assets"
                params={{ org: org || 'demo' }}
                search={{ asset_type_code: currentAssetType?.code } as any}
                className="text-primary hover:underline"
                data-testid="asset-type-delete-blocked-live-link"
              >
                {t('asset_types.delete_blocked_assets_live', { count: usage.liveCount })}
              </Link>
              {' · '}
              <Link
                to="/$org/assets"
                params={{ org: org || 'demo' }}
                search={{ asset_type_code: currentAssetType?.code, archived: 'true' } as any}
                className="text-primary hover:underline"
                data-testid="asset-type-delete-blocked-archived-link"
              >
                {t('asset_types.delete_blocked_assets_archived', { count: usage.archivedCount })}
              </Link>
            </p>
          )}
          {childTypeCount > 0 && <p data-testid="asset-type-delete-blocked-children">{t('asset_types.delete_blocked_children')}</p>}
          {!isArchived && <p className="text-muted-foreground">{t('asset_types.delete_blocked_hint')}</p>}
        </div>
      )}

      <nav className="mb-6">
        <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground">
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              data-testid={tab.testId}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                activeTab === tab.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'hover:bg-background/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Basic info — click-to-edit */}
          <Card>
            <CardContent className="pt-6 space-y-6">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('asset_types.field_name')}</label>
                {canWrite && isEditingName ? (
                  <div
                    className="mt-2 space-y-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSaveName()
                      } else if (e.key === 'Escape') {
                        setIsEditingName(false)
                      }
                    }}
                  >
                    <MultilingualInput
                      value={nameDraft}
                      onChange={setNameDraft}
                      data-testid="asset-type-name-input"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveName} data-testid="asset-type-name-save">
                        <AppIcon path={mdiCheck} size="sm" className="mr-1" />
                        {t('common:actions.save')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditingName(false)}
                        data-testid="asset-type-name-cancel"
                      >
                        {t('common:actions.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : canWrite ? (
                  <p
                    className="text-lg text-foreground mt-1 cursor-pointer hover:bg-muted/40 rounded px-1 -mx-1"
                    title={t('common:actions.edit')}
                    data-testid="asset-type-name"
                    onClick={() => {
                      setNameDraft(getAssetTypeNameObject(currentAssetType))
                      setIsEditingName(true)
                    }}
                  >
                    {getAssetTypeName(currentAssetType)}
                  </p>
                ) : (
                  <p className="text-lg text-foreground mt-1 px-1 -mx-1" data-testid="asset-type-name">
                    {getAssetTypeName(currentAssetType)}
                  </p>
                )}
              </div>

              {/* Description — bilingual, click-to-edit like the name */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('asset_types.field_description')}</label>
                {canWrite && isEditingDescription ? (
                  <div
                    className="mt-2 space-y-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSaveDescription()
                      } else if (e.key === 'Escape') {
                        setIsEditingDescription(false)
                      }
                    }}
                  >
                    <MultilingualInput
                      value={descriptionDraft}
                      onChange={setDescriptionDraft}
                      placeholder={t('asset_types.description_placeholder')}
                      data-testid="asset-type-description"
                    />
                    {descriptionSaveFailed && (
                      <p className="text-sm text-destructive" data-testid="asset-type-description-error">
                        {t('asset_types.description_save_error')}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveDescription}
                        disabled={updateAssetTypeMutation.isPending}
                        data-testid="asset-type-description-save"
                      >
                        <AppIcon path={mdiCheck} size="sm" className="mr-1" />
                        {t('common:actions.save')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditingDescription(false)}
                        data-testid="asset-type-description-cancel"
                      >
                        {t('common:actions.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p
                    className={`mt-1 px-1 -mx-1 whitespace-pre-line ${
                      currentAssetType.description && getLocalizedName(currentAssetType.description, i18n.language)
                        ? 'text-foreground'
                        : 'text-muted-foreground italic'
                    } ${canWrite ? 'cursor-pointer hover:bg-muted/40 rounded' : ''}`}
                    title={canWrite ? t('common:actions.edit') : undefined}
                    data-testid="asset-type-description-display"
                    onClick={canWrite ? () => {
                      setDescriptionDraft(toBilingualForm(currentAssetType.description))
                      setDescriptionSaveFailed(false)
                      setIsEditingDescription(true)
                    } : undefined}
                  >
                    {getLocalizedName(currentAssetType.description, i18n.language) || t('asset_types.description_empty')}
                  </p>
                )}
              </div>

              {/* Icon — the glyph every tree row, picker and header draws for
                  this type (spec 2026-09-17-04). Clearing hands it back to the
                  family default. */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('asset_types.field_icon', 'Icon')}
                </label>
                <div className="mt-2">
                  {canWrite ? (
                    <AssetTypeIconPicker
                      type={currentAssetType as AssetTypeLike}
                      value={(currentAssetType as { icon?: string | null }).icon}
                      disabled={updateAssetTypeMutation.isPending}
                      onChange={handleSaveIcon}
                    />
                  ) : (
                    <AssetTypeTile type={currentAssetType as AssetTypeLike} size="sm" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('asset_types.icon_hint', 'Drawn in the asset tree, the pickers and the asset header. Its colour comes from the type\'s family.')}
                </p>
              </div>

              {/* Code (read-only) + Created (read-only) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('asset_types.field_code')}</label>
                  <p className="text-lg text-foreground mt-1">
                    <code className="bg-muted px-2 py-1 rounded text-sm">{currentAssetType.code}</code>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('asset_types.field_created')}</label>
                  <p className="text-foreground mt-1" data-testid="asset-type-created">
                    {formatCreated(currentAssetType.created_at)}
                  </p>
                </div>
              </div>

              {/* Counters */}
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('asset_types.field_properties_count')}</label>
                <p className="text-foreground mt-1 flex items-center gap-2">
                  <Badge variant="secondary">{assetTypeProperties.length}</Badge>
                  {t('asset_types.properties_count', { count: assetTypeProperties.length })}
                </p>
              </div>

              {/* Toggles committed on change */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t">
                <div>
                  <div className="flex items-center space-x-2">
                    {/* Read-only callers keep seeing the value — the box just
                        stops being actionable. */}
                    <Checkbox
                      id="should-receive-picture"
                      checked={shouldReceivePicture}
                      onCheckedChange={canWrite ? (checked) => handleTogglePicture(Boolean(checked)) : undefined}
                      disabled={!canWrite || updateAssetTypeMutation.isPending}
                      data-testid="should-receive-picture-checkbox"
                    />
                    <Label htmlFor="should-receive-picture" className="text-sm font-medium">
                      {t('asset_types.picture_checkbox')}
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('asset_types.picture_hint')}
                  </p>
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="multiple-quantity"
                      checked={isMultipleQuantity}
                      onCheckedChange={canWrite ? (checked) => handleToggleMultiple(Boolean(checked)) : undefined}
                      disabled={!canWrite || updateAssetTypeMutation.isPending}
                      data-testid="asset-type-multiple-toggle"
                    />
                    <Label htmlFor="multiple-quantity" className="text-sm font-medium">
                      {t('asset_types.multiple_checkbox')}
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('asset_types.multiple_hint')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Default document folder layout (spec 2026-08-01-05) */}
          <Card>
            <CardHeader>
              <CardTitle>{t('asset_types.folder_layout_title')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('asset_types.folder_layout_description')}
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              <Label htmlFor="asset-type-folder-layout" className="text-sm font-medium text-muted-foreground">
                {t('asset_types.folder_layout_label')}
              </Label>
              {/* Read-only: the picker keeps showing which layout is in
                  force, it simply cannot be changed. */}
              <Select
                value={(currentAssetType as any)?.document_folder_layout_uid || NO_FOLDER_LAYOUT}
                onValueChange={canWrite ? handleChangeFolderLayout : undefined}
                disabled={!canWrite || updateAssetTypeMutation.isPending}
              >
                <SelectTrigger
                  id="asset-type-folder-layout"
                  className="w-full md:w-96"
                  data-testid="asset-type-folder-layout-select"
                >
                  <SelectValue />
                </SelectTrigger>
                {/* Capped + scrollable: an org can hold dozens of layouts. */}
                <SelectContent className="max-h-72 [&>div]:!h-auto [&>div]:max-h-[17rem] [&>div]:overflow-y-auto">
                  <SelectItem value={NO_FOLDER_LAYOUT} data-testid="asset-type-folder-layout-option-none">
                    {t('asset_types.folder_layout_none')}
                  </SelectItem>
                  {folderLayouts.map(layout => (
                    <SelectItem
                      key={layout.uid as string}
                      value={layout.uid as string}
                      data-testid={`asset-type-folder-layout-option-${layout.code}`}
                    >
                      <span>{getLocalizedName(layout.name as Record<string, string>, i18n.language) || layout.code}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{layout.code}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground" data-testid="asset-type-folder-layout-hint">
                {t('asset_types.folder_layout_hint')}
              </p>
              <Link
                to="/$org/folder-layouts"
                params={{ org: org || 'demo' }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
                data-testid="asset-type-folder-layout-manage-link"
              >
                {t('asset_types.folder_layout_manage_link')}
              </Link>
            </CardContent>
          </Card>

          {/* Possible parent types — chips with × */}
          <Card>
            <CardHeader>
              <CardTitle>{t('asset_types.parent_types_title')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('asset_types.parent_types_description')}
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2">
                {possibleParentTypes.length === 0 && !isAddingParent && (
                  <span className="text-sm text-muted-foreground italic">
                    {t('asset_types.no_parent_restrictions')}
                  </span>
                )}
                {possibleParentTypes.map((parentTypeUid) => {
                  const parentType = assetTypes.find(at => at.uid === parentTypeUid)
                  return (
                    <span
                      key={parentTypeUid}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                      data-testid={`parent-type-chip-${parentTypeUid}`}
                    >
                      {parentType ? (
                        <Link
                          to="/$org/asset-types/$assetTypeUid"
                          params={{ org: org || 'demo', assetTypeUid: parentTypeUid }}
                          search={{ tab: 'overview' }}
                          className="inline-flex items-center gap-1 hover:underline underline-offset-2"
                          data-testid={`parent-type-link-${parentType.code}`}
                        >
                          <span className="font-medium">{getAssetTypeName(parentType)}</span>
                          <code className="text-xs text-muted-foreground">({parentType.code})</code>
                        </Link>
                      ) : (
                        <span className="font-medium">Unknown</span>
                      )}
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => handleRemoveParentType(parentTypeUid)}
                          className="ml-1 text-muted-foreground hover:text-destructive"
                          aria-label="Remove"
                          data-testid={`remove-parent-type-${parentTypeUid}`}
                        >
                          <AppIcon path={mdiClose} size="xs" />
                        </button>
                      )}
                    </span>
                  )
                })}
                {!canWrite ? null : isAddingParent ? (
                  <div className="inline-flex items-center gap-2" data-testid="add-parent-select-wrapper">
                    <Select
                      onValueChange={(uid) => handleAddParentType(uid)}
                      disabled={availableParentTypes.filter(at => !possibleParentTypes.includes(at.uid)).length === 0}
                    >
                      <SelectTrigger className="h-8 w-[220px]" data-testid="parent-type-select">
                        <SelectValue placeholder={t('asset_types.parent_type_select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableParentTypes
                          .filter(at => !possibleParentTypes.includes(at.uid))
                          .map((assetType) => (
                            <SelectItem key={assetType.uid} value={assetType.uid}>
                              <div className="flex items-center gap-2">
                                <span>{getAssetTypeName(assetType)}</span>
                                <Badge variant="outline" className="text-xs">
                                  {assetType.code}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsAddingParent(false)}
                      data-testid="cancel-add-parent"
                    >
                      <AppIcon path={mdiClose} size="sm" />
                    </Button>
                  </div>
                ) : (
                  possibleParentTypes.length < 5 && availableParentTypes.filter(at => !possibleParentTypes.includes(at.uid)).length > 0 && (
                    <button
                      type="button"
                      onClick={() => setIsAddingParent(true)}
                      className="inline-flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 px-3 py-1 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
                      data-testid="add-parent-type"
                    >
                      <AppIcon path={mdiPlus} size="xs" />
                      {t('asset_types.add_parent_type')}
                    </button>
                  )
                )}
              </div>
            </CardContent>
          </Card>

          {/* Both hierarchies around this type, in one tree (issue #1469) */}
          <Card>
            <CardContent className="pt-6">
              <AssetTypeHierarchy
                org={org || 'demo'}
                types={assetTypes}
                mode={treeMode}
                onModeChange={(m) => navigate({ search: (prev) => ({ ...prev, tree: m === 'children' ? m : undefined }) })}
                getName={getAssetTypeName}
                focusUid={assetTypeUid}
              />
            </CardContent>
          </Card>

          {/* Types inheriting from this one */}
          <Card>
            <CardHeader>
              <CardTitle>{t('asset_types.inheriting_types_title')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('asset_types.inheriting_types_description')}
              </p>
            </CardHeader>
            <CardContent>
              <AssetTypeChips
                org={org || 'demo'}
                types={inheritingTypes}
                getName={getAssetTypeName}
                emptyLabel={t('asset_types.no_inheriting_types')}
                testId="inheriting-types"
              />
            </CardContent>
          </Card>

          {/* Types that can be placed below this one */}
          <Card>
            <CardHeader>
              <CardTitle>{t('asset_types.child_types_title')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('asset_types.child_types_description')}
              </p>
            </CardHeader>
            <CardContent>
              <AssetTypeChips
                org={org || 'demo'}
                types={possibleChildTypes}
                getName={getAssetTypeName}
                emptyLabel={t('asset_types.no_child_types')}
                testId="possible-child-types"
              />
            </CardContent>
          </Card>

          {/* Lifespan reference */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={mdiClockOutline} size="md" />
                {t('asset_types.lifespan_reference_title')}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('asset_types.lifespan_reference_description')}
              </p>
            </CardHeader>
            <CardContent>
              {canWrite && isEditingLifespan ? (
                <div className="space-y-4">
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="min-w-full divide-y divide-border">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            {t('asset_types.lifespan_condition')}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            {t('asset_types.lifespan_min')}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            {t('asset_types.lifespan_max')}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            {t('asset_types.lifespan_design_life')}
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                            {t('asset_types.lifespan_source')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {lifespanEntries.map((entry) => (
                          <tr key={entry.condition}>
                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                              {t(`asset_types.lifespan_condition_${entry.condition}`)}
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min={0}
                                value={entry.offset_years_min || ''}
                                onChange={(e) => handleUpdateLifespanEntry(entry.condition, 'offset_years_min', parseInt(e.target.value) || 0)}
                                className="w-20"
                                data-testid={`lifespan-min-${entry.condition}`}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min={0}
                                value={entry.offset_years_max || ''}
                                onChange={(e) => handleUpdateLifespanEntry(entry.condition, 'offset_years_max', parseInt(e.target.value) || 0)}
                                className="w-20"
                                data-testid={`lifespan-max-${entry.condition}`}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                type="number"
                                min={0}
                                value={entry.theoretical_lifespan_years || ''}
                                onChange={(e) => handleUpdateLifespanEntry(entry.condition, 'theoretical_lifespan_years', parseInt(e.target.value) || 0)}
                                className="w-20"
                                data-testid={`lifespan-design-${entry.condition}`}
                              />
                            </td>
                            <td className="px-4 py-2">
                              <Input
                                value={entry.source}
                                onChange={(e) => handleUpdateLifespanEntry(entry.condition, 'source', e.target.value)}
                                className="w-28"
                                placeholder="e.g. LFNM"
                                data-testid={`lifespan-source-${entry.condition}`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={handleSaveLifespan}
                      disabled={updateAssetTypeMutation.isPending}
                      data-testid="save-lifespan"
                    >
                      {updateAssetTypeMutation.isPending ? t('asset_types.saving') : t('common:actions.save')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEditingLifespan}
                      data-testid="cancel-lifespan"
                    >
                      {t('common:actions.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {lifespanEntries.length > 0 && lifespanEntries.some(e => e.theoretical_lifespan_years > 0) ? (
                    <div className="overflow-x-auto rounded-md border border-border">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                              {t('asset_types.lifespan_condition')}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                              {t('asset_types.lifespan_min')}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                              {t('asset_types.lifespan_max')}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                              {t('asset_types.lifespan_design_life')}
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                              {t('asset_types.lifespan_source')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {lifespanEntries.filter(e => e.theoretical_lifespan_years > 0).map((entry) => (
                            <tr key={entry.condition} className="hover:bg-muted/50">
                              <td className="px-4 py-2 whitespace-nowrap text-sm">
                                {t(`asset_types.lifespan_condition_${entry.condition}`)}
                              </td>
                              <td className="px-4 py-2 text-sm text-muted-foreground">{entry.offset_years_min}</td>
                              <td className="px-4 py-2 text-sm text-muted-foreground">{entry.offset_years_max}</td>
                              <td className="px-4 py-2 text-sm text-muted-foreground">{entry.theoretical_lifespan_years}</td>
                              <td className="px-4 py-2 text-sm text-muted-foreground">{entry.source || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-muted/30 rounded-md">
                      <p className="text-sm text-muted-foreground">
                        {t('asset_types.lifespan_reference_checkbox')}
                      </p>
                    </div>
                  )}
                  {canWrite && (
                    <Button
                      onClick={() => {
                        if (lifespanEntries.length === 0) {
                          setLifespanEntries([...DEFAULT_LIFESPAN_ENTRIES])
                        }
                        setIsEditingLifespan(true)
                      }}
                      data-testid="edit-lifespan"
                    >
                      <AppIcon path={mdiCog} size="sm" className="mr-2" />
                      {t('common:actions.edit')}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'properties' && (
        <div className="space-y-6">
          {/* Add Property Section — pure write affordance, hidden outright
              for a read-only caller. */}
          {canWrite && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('asset_types.add_property_title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 items-end">
                <div className="flex-1 max-w-sm">
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    {t('asset_types.select_property')}
                  </label>
                  <ApiSearchableSelect
                    value={selectedProperty}
                    placeholder={t('asset_types.property_placeholder')}
                    searchPlaceholder={t('asset_types.property_search')}
                    emptyMessage={t('asset_types.property_not_found')}
                    onSearch={searchProperties}
                    initialOptions={initialPropertyOptions}
                    allOptions={allAvailableOptions}
                    onOptionSelect={handlePropertySelect}
                    onValueChange={(value) => setSelectedProperty(value || '')}
                    className="w-full max-w-sm"
                    minSearchLength={2}
                    debounceMs={150}
                    disabled={availableProperties.length === 0}
                    data-testid="property-select"
                  />
                </div>
                <Button
                  onClick={handleAddProperty}
                  disabled={!selectedProperty || addPropertyMutation.isPending}
                  data-testid="add-property-button"
                >
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {addPropertyMutation.isPending ? t('asset_types.adding_property') : t('asset_types.add_property')}
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Associated Properties Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t('asset_types.associated_properties')}</CardTitle>
                <Button
                  variant={showInheritedProperties ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowInheritedProperties(!showInheritedProperties)}
                  data-testid="toggle-inherited-properties"
                >
                  {showInheritedProperties ? t('asset_types.hide_inherited') : t('asset_types.show_inherited')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-card shadow overflow-x-auto rounded-md border border-border">
                <table className="min-w-[800px] divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('asset_types.field_name')}
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('asset_types.field_code')}
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('asset_types.column_type')}
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('asset_types.column_requirement')}
                      </th>
                      <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('asset_types.column_weight')}
                      </th>
                      {showInheritedProperties && (
                        <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                          {t('asset_types.column_origin')}
                        </th>
                      )}
                      {showInheritedProperties && (
                        <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                          {t('asset_types.column_assets')}
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {t('asset_types.column_actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {(() => {
                      const isLoading = isLoadingProperties
                      const totalCols = showInheritedProperties ? 8 : 6

                      if (isLoading) {
                        return (
                          <tr>
                            <td colSpan={totalCols} className="px-6 py-12 text-center">
                              <div className="flex items-center justify-center">
                                <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                                <span className="text-muted-foreground">{t('asset_types.loading_properties')}</span>
                              </div>
                            </td>
                          </tr>
                        )
                      }

                      if (propertiesError) {
                        return (
                          <tr>
                            <td colSpan={totalCols} className="px-6 py-12 text-center">
                              <div className="flex flex-col items-center justify-center">
                                <svg className="h-12 w-12 text-destructive mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-destructive mb-2">
                                  {t('asset_types.error_properties')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {propertiesError instanceof Error ? propertiesError.message : t('asset_types.unknown_error')}
                                </p>
                              </div>
                            </td>
                          </tr>
                        )
                      }

                      const propertiesToDisplay = assetTypeProperties.map((property: any) => {
                        if (showInheritedProperties) {
                          const isInherited = property.asset_type_uid && property.asset_type_uid !== assetTypeUid
                          const originAssetType = isInherited
                            ? assetTypes.find(at => at.uid === property.asset_type_uid)
                            : null
                          return {
                            ...property,
                            isInherited,
                            originAssetType,
                            associationId: null
                          }
                        } else {
                          return {
                            ...property,
                            isInherited: false,
                            originAssetType: null,
                            associationId: null
                          }
                        }
                      })

                      if (propertiesToDisplay.length === 0) {
                        return (
                          <tr>
                            <td colSpan={totalCols} className="px-6">
                              {/* One empty state, not two stacked ones: the type
                                  has no property, and the second line says what
                                  to do about it — a hint when properties exist to
                                  pick from, the first-run CTA when none do. */}
                              <EmptyState
                                className="py-12"
                                tone={
                                  canWrite && !showInheritedProperties && availableProperties.length === 0
                                    ? 'solid'
                                    : 'soft'
                                }
                                icon={mdiShapeOutline}
                                title={
                                  showInheritedProperties
                                    ? t('asset_types.no_properties_with_inherited')
                                    : t('asset_types.no_properties')
                                }
                                description={
                                  canWrite && !showInheritedProperties && availableProperties.length > 0
                                    ? t('asset_types.use_form_to_add')
                                    : undefined
                                }
                                action={
                                  canWrite && !showInheritedProperties && availableProperties.length === 0 ? (
                                    <div className="flex flex-col items-center gap-3">
                                      <span className="max-w-[36ch] text-xs text-muted-foreground">
                                        {t('asset_types.no_properties_available')}
                                      </span>
                                      <Link to={`/${org}/properties/create` as any}>
                                        <Button data-testid="create-property-button">
                                          <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                          {t('asset_types.create_property_first')}
                                        </Button>
                                      </Link>
                                    </div>
                                  ) : null
                                }
                              />
                            </td>
                          </tr>
                        )
                      }

                      return propertiesToDisplay.map((property: any) => (
                        <tr key={`${property.uid}-${property.isInherited ? 'inherited' : 'direct'}`} className="hover:bg-muted/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/${org}/properties/${property.uid}` as any}
                                className="font-medium hover:text-primary"
                                data-testid={`property-name-${property.uid}`}
                              >
                                {getPropertyName(property)}
                              </Link>
                              {property.isInherited && (
                                <Badge variant="outline" className="text-xs">
                                  {t('asset_types.badge_inherited')}
                                </Badge>
                              )}
                            </div>
                            {getPropertyDescription(property) && (
                              <p className="text-xs text-muted-foreground mt-1 truncate max-w-xs">
                                {getPropertyDescription(property)}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            <code className="text-xs bg-muted px-2 py-1 rounded">
                              {property.code}
                            </code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            <Badge variant="secondary">
                              {DATA_TYPE_LABELS[property.data_type as keyof typeof DATA_TYPE_LABELS] || property.data_type}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {/* Inherited rows were never editable here; a
                                read-only caller gets the same static badge so
                                the requirement stays legible. */}
                            {property.isInherited || !canWrite ? (
                              <Badge
                                variant={
                                  property.requirement === 'mandatory' ? 'destructive' :
                                  property.requirement === 'recommended' ? 'default' : 'secondary'
                                }
                                data-testid={`requirement-value-${property.uid}`}
                              >
                                {t(`asset_types.requirement_${property.requirement || 'optional'}`)}
                              </Badge>
                            ) : (
                              <Select
                                value={property.requirement || 'optional'}
                                onValueChange={async (value) => {
                                  try {
                                    await updatePropertyMutation.mutateAsync(property.uid, { requirement: value as 'optional' | 'recommended' | 'mandatory' })
                                  } catch (error) {
                                    console.error('Error updating requirement:', error)
                                  }
                                }}
                              >
                                <SelectTrigger
                                  className="w-[140px] h-8"
                                  data-testid={`requirement-select-${property.uid}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="optional">{t('asset_types.requirement_optional')}</SelectItem>
                                  <SelectItem value="recommended">{t('asset_types.requirement_recommended')}</SelectItem>
                                  <SelectItem value="mandatory">{t('asset_types.requirement_mandatory')}</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {/* Same rule as the requirement cell: the weight is
                                always shown, click-to-edit only for a writer. */}
                            {property.isInherited || !canWrite ? (
                              <span
                                className={property.completeness_weight != null ? 'text-foreground' : 'text-muted-foreground italic'}
                                data-testid={`weight-value-${property.uid}`}
                              >
                                {property.completeness_weight != null ? property.completeness_weight : t('asset_types.weight_auto')}
                              </span>
                            ) : editingWeightUid === property.uid ? (
                              <Input
                                type="number"
                                min={0}
                                max={100}
                                className="w-[80px] h-8"
                                value={editingWeightValue}
                                autoFocus
                                data-testid={`weight-input-${property.uid}`}
                                onChange={(e) => setEditingWeightValue(e.target.value)}
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    const val = editingWeightValue.trim() === '' ? undefined : Number(editingWeightValue)
                                    if (val !== undefined && (isNaN(val) || val < 0 || val > 100)) return
                                    try {
                                      await updatePropertyMutation.mutateAsync(property.uid, { completeness_weight: val })
                                    } catch (error) {
                                      console.error('Error updating weight:', error)
                                    }
                                    setEditingWeightUid(null)
                                  } else if (e.key === 'Escape') {
                                    setEditingWeightUid(null)
                                  }
                                }}
                                onBlur={async () => {
                                  const val = editingWeightValue.trim() === '' ? undefined : Number(editingWeightValue)
                                  if (val !== undefined && (isNaN(val) || val < 0 || val > 100)) {
                                    setEditingWeightUid(null)
                                    return
                                  }
                                  try {
                                    await updatePropertyMutation.mutateAsync(property.uid, { completeness_weight: val })
                                  } catch {
                                    // Query invalidation handles refresh
                                  }
                                  setEditingWeightUid(null)
                                }}
                              />
                            ) : (
                              <span
                                className={`cursor-pointer hover:text-primary ${property.completeness_weight != null ? 'text-foreground' : 'text-muted-foreground italic'}`}
                                data-testid={`weight-value-${property.uid}`}
                                onClick={() => {
                                  setEditingWeightUid(property.uid)
                                  setEditingWeightValue(property.completeness_weight != null ? String(property.completeness_weight) : '')
                                }}
                              >
                                {property.completeness_weight != null ? property.completeness_weight : t('asset_types.weight_auto')}
                              </span>
                            )}
                          </td>
                          {showInheritedProperties && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              {property.originAssetType ? (
                                <Link
                                  to={`/${org}/asset-types/${property.originAssetType.uid}` as any}
                                  className="text-primary hover:underline"
                                >
                                  {getAssetTypeName(property.originAssetType)}
                                </Link>
                              ) : (
                                <span className="text-primary font-medium">{t('asset_types.current_type')}</span>
                              )}
                            </td>
                          )}
                          {showInheritedProperties && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                              <Badge variant="outline">
                                {t('asset_types.assets_count', { count: assetsInvolved.length })}
                              </Badge>
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <Link to={`/${org}/properties/${property.uid}` as any}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`view-property-${property.uid}`}
                                >
                                  <AppIcon path={mdiEyeOutline} size="sm" />
                                </Button>
                              </Link>
                              {canWrite && !property.isInherited && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveProperty(property.uid, getPropertyName(property))}
                                  disabled={removePropertyMutation.isPending}
                                  className="text-destructive hover:text-destructive disabled:opacity-50"
                                  data-testid={`remove-property-${property.uid}`}
                                >
                                  <AppIcon path={mdiClose} size="sm" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    })()}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

/** Asset types as chips, each linking to its own page. */
function AssetTypeChips({
  org,
  types,
  getName,
  emptyLabel,
  testId,
}: {
  org: string
  types: (AssetTypeLike & { uid: string })[]
  getName: (assetType: AssetTypeLike) => string
  emptyLabel: string
  testId: string
}) {
  if (types.length === 0) {
    return (
      <span className="text-sm text-muted-foreground italic" data-testid={`${testId}-empty`}>
        {emptyLabel}
      </span>
    )
  }
  const sorted = [...types].sort((a, b) => getName(a).localeCompare(getName(b)))
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      {sorted.map(type => (
        <Link
          key={type.uid}
          to="/$org/asset-types/$assetTypeUid"
          params={{ org, assetTypeUid: type.uid }}
          search={{ tab: 'overview' }}
          className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-sm hover:bg-muted/70"
          data-testid={`${testId}-chip-${type.code}`}
        >
          <AssetTypeTile type={type} size="sm" />
          <span className="font-medium">{getName(type)}</span>
          <code className="text-xs text-muted-foreground">({type.code})</code>
        </Link>
      ))}
    </div>
  )
}
